import React, { lazy } from 'react';
import { RouteProps } from 'react-router-dom';

import Login from '../pages/auth/Login';
import { LANDING_PAGES } from '../content/landing';
import { isComingSoon } from '../preview/previewAccess';

const LANDING = {
	HOME: lazy(() => import('../pages/home/HomePage')),
};
const PRODUCTS = {
	PRODUCTS: lazy(() => import('../pages/Products/ProductsPage')),
}
const PRODUCT_DETAIL = {
	PRODUCT_DETAIL_PAGE: lazy(() => import('../pages/Products/ProductDetailPage')),
};
const ACCOUNT = {
	MY_AVATARS: lazy(() => import('../pages/account/MyAvatarsPage')),
	MY_ORDERS: lazy(() => import('../pages/account/MyOrdersPage')),
	MY_CART: lazy(() => import('../pages/account/MyCartPage')),
	CREDIT_PURCHASE_COMPLETE: lazy(() => import('../pages/account/CreditPurchaseCompletePage')),
	ACCOUNT_SETTINGS: lazy(() => import('../pages/account/AccountSettingsPage')),
};
const STORIES = {
	/** @deprecated Use CUSTOMIZE.CUSTOMIZE_PAGE instead — kept for backward compatibility */
	CUSTOMIZE_STORY_PAGE: lazy(() => import('../pages/Stories/StoryPage')),
};
const SHIPING = {
	SHIPING_PAGE: lazy(() => import('../pages/shipment/ShipmentPage')),
}

const PAYMENT = {
	COMPLETE: lazy(() => import('../pages/Payment/CompletePage')),
	REVIEW: lazy(() => import('../pages/Payment/ReviewPage')),
}

const LEGAL = {
	PRIVACY: lazy(() => import('../pages/legal/PrivacyPolicyPage')),
	TERMS: lazy(() => import('../pages/legal/TermsOfServicePage')),
	TERMS_OF_SALE: lazy(() => import('../pages/legal/TermsOfSalePage')),
	REFUNDS: lazy(() => import('../pages/legal/RefundPolicyPage')),
};

/**
 * Editorial/SEO surface. These are the pages that get prerendered to static
 * HTML at build time (see scripts/prerender.js) and are the intended entry
 * point for organic search traffic.
 */
const CONTENT = {
	LANDING: lazy(() => import('../pages/content/LandingPage')),
	BLOG_INDEX: lazy(() => import('../pages/content/BlogIndexPage')),
	BLOG_POST: lazy(() => import('../pages/content/BlogPostPage')),
};

/**
 * One route per landing page, all served by the same component, which resolves
 * its content by pathname. Declared from the content registry so adding a
 * landing page requires no change here.
 */
const landingRoutes: RouteProps[] = LANDING_PAGES.map((p) => ({
	path: p.path,
	element: <CONTENT.LANDING />,
}));

const contentRoutes: RouteProps[] = [
	...landingRoutes,
	{ path: '/blog', element: <CONTENT.BLOG_INDEX /> },
	{ path: '/blog/:slug', element: <CONTENT.BLOG_POST /> },
];

const COMING_SOON_PAGE = lazy(() => import('../pages/comingsoon/ComingSoonPage'));

const AUTH = {
	PAGE_404: lazy(() => import('../pages/auth/Page404')),
	FORGOT_PASSWORD: lazy(() => import('../pages/auth/ForgotPasswordPage')),
	RESET_PASSWORD: lazy(() => import('../pages/auth/ResetPasswordPage')),
};


const presentation: RouteProps[] = [
	/**
	 * Landing
	 */
	{
		path: '/',
		element: <LANDING.HOME />,
	},
	/**
	 * Product detail page: /products/:type/:id
	 */
	{
		path: '/products/:type/:id',
		element: <PRODUCT_DETAIL.PRODUCT_DETAIL_PAGE />,
	},
	/**
	 * Backward compatibility: /stories/:id redirects to story customization
	 */
	{
		path: '/stories/:id',
		element: <STORIES.CUSTOMIZE_STORY_PAGE />,
	},
	/**
	 * Shiping page
	 */
	{
		path: '/order-summary',
		element: <SHIPING.SHIPING_PAGE />,
	},
	/**
	 * Payment page
	 */
	{
		path: '/complete',
		element: <PAYMENT.COMPLETE />,
	},
	/**
	 * Customer book review / approval — /review/:orderId (token via ?t= or localStorage)
	 */
	{
		path: '/review/:orderId',
		element: <PAYMENT.REVIEW />,
	},
	/**
	 * Products page
	*/
	{
		path: '/products',
		element: <PRODUCTS.PRODUCTS />,
	},
	/**
	 * Auth
	 */
	{
		path: 'auth',
		element: <Login />,
	},
	{
		path: 'auth',
		element: <Login isSignUp />,
	},
	{
		path: '/forgot-password',
		element: <AUTH.FORGOT_PASSWORD />,
	},
	{
		path: '/reset-password',
		element: <AUTH.RESET_PASSWORD />,
	},
	/**
	 * My Avatars (profile page)
	 */
	{
		path: '/my-avatars',
		element: <ACCOUNT.MY_AVATARS />,
	},
	/**
	 * My Orders
	 */
	{
		path: '/my-orders',
		element: <ACCOUNT.MY_ORDERS />,
	},
	/**
	 * My Cart
	 */
	{
		path: '/my-cart',
		element: <ACCOUNT.MY_CART />,
	},
	/**
	 * Credit purchase complete
	 */
	{
		path: '/credits/complete',
		element: <ACCOUNT.CREDIT_PURCHASE_COMPLETE />,
	},
	/**
	 * Account settings (GDPR: download data, delete account)
	 */
	{
		path: '/account-settings',
		element: <ACCOUNT.ACCOUNT_SETTINGS />,
	},
	/**
	 * Privacy policy (GDPR Art. 13/14)
	 */
	{
		path: '/privacy',
		element: <LEGAL.PRIVACY />,
	},
	/**
	 * Terms of Service
	 */
	{
		path: '/terms',
		element: <LEGAL.TERMS />,
	},
	/**
	 * Terms of Sale (consumer contract) and Refund Policy
	 */
	{
		path: '/terms-of-sale',
		element: <LEGAL.TERMS_OF_SALE />,
	},
	{
		path: '/refund-policy',
		element: <LEGAL.REFUNDS />,
	},

	/**
	 * Gift guides, landing pages and blog — the organic search surface.
	 */
	...contentRoutes,

	/**
	 * Catch-all: unknown URLs render the (noindex) 404 page.
	 */
	{ path: '*', element: <AUTH.PAGE_404 /> },

	/** ************************************************** */

];

/**
 * Pre-launch mode: REACT_APP_COMING_SOON=true replaces the whole site with the
 * coming-soon page, except for browsers holding a preview pass (see
 * preview/previewAccess.ts). Legal pages stay reachable (required — the early-access
 * form collects emails and links the privacy policy).
 *
 * The editorial content pages also stay reachable, deliberately: search
 * rankings take months to build, so the guides and blog posts start earning
 * authority while the store itself is still behind the coming-soon gate. Their
 * calls-to-action lead to the coming-soon page, which is the intended
 * pre-launch funnel. Remove `...contentRoutes` here if you would rather keep
 * the content unpublished until launch.
 */
const comingSoon: RouteProps[] = [
	{ path: '/privacy', element: <LEGAL.PRIVACY /> },
	{ path: '/terms', element: <LEGAL.TERMS /> },
	{ path: '/terms-of-sale', element: <LEGAL.TERMS_OF_SALE /> },
	{ path: '/refund-policy', element: <LEGAL.REFUNDS /> },
	...contentRoutes,
	{ path: '*', element: <COMING_SOON_PAGE /> },
];

// Chosen at render time (not module load) so a preview pass resolved in
// index.tsx before the first render takes effect.
const getContents = (): RouteProps[] => (isComingSoon() ? comingSoon : presentation);

export default getContents;
